import React, { useMemo, useState } from "react";
import SectionCard from "./SectionCard";
import { Input, Textarea } from "./Fields";

import {
  updateSettingsAlert,
  disableSettingsAlert,
} from "../../../../services/settingsServices";

const TYPE_META = {
  info: {
    label: "Info",
    box: "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-700/50 dark:bg-sky-900/20 dark:text-sky-100",
    pill: "bg-sky-600 text-white",
  },
  warning: {
    label: "Alerte",
    box: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-100",
    pill: "bg-amber-600 text-white",
  },
  danger: {
    label: "Urgent",
    box: "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-700/50 dark:bg-rose-900/20 dark:text-rose-100",
    pill: "bg-rose-600 text-white",
  },
  success: {
    label: "Succès",
    box: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/50 dark:bg-emerald-900/20 dark:text-emerald-100",
    pill: "bg-emerald-600 text-white",
  },
};

export default function AlertSection({ form, setForm, setError }) {
  const [saving, setSaving] = useState(false);

  const meta = TYPE_META[form.alert_type] || TYPE_META.info;

  const preview = useMemo(() => {
    const active = !!form.alert_is_active;
    const title = (form.alert_title || "").trim();
    const message = (form.alert_message || "").trim();
    const linkLabel = (form.alert_link_label || "").trim();
    const linkUrl = (form.alert_link_url || "").trim();

    return { active, title, message, linkLabel, linkUrl };
  }, [
    form.alert_is_active,
    form.alert_title,
    form.alert_message,
    form.alert_link_label,
    form.alert_link_url,
  ]);

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const onToggleActive = () => {
    setForm((current) => ({
      ...current,
      alert_is_active: !current.alert_is_active,
    }));
  };

  const buildPayload = () => {
    const toIsoOrNull = (value) => {
      if (!value) return null;

      const d = new Date(value);

      if (Number.isNaN(d.getTime())) return null;

      return d.toISOString();
    };

    return {
      alert_is_active: !!form.alert_is_active,
      alert_type: form.alert_type || "info",
      alert_title: form.alert_title || null,
      alert_message: form.alert_message || null,
      alert_link_label: form.alert_link_label || null,
      alert_link_url: form.alert_link_url || null,
      alert_start_at: toIsoOrNull(form.alert_start_at),
      alert_end_at: toIsoOrNull(form.alert_end_at),
    };
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);

    try {
      const payload = buildPayload();
      const res = await updateSettingsAlert(payload);

      if (!res?.ok) {
        throw new Error("updateSettingsAlert failed");
      }

      const alert = res?.alert || {};

      setForm((current) => ({
        ...current,
        alert_is_active: alert.is_active ?? payload.alert_is_active,
        alert_type: alert.type || payload.alert_type,
        alert_title: alert.title ?? payload.alert_title ?? "",
        alert_message: alert.message ?? payload.alert_message ?? "",
        alert_link_label: alert.link_label ?? payload.alert_link_label ?? "",
        alert_link_url: alert.link_url ?? payload.alert_link_url ?? "",
      }));
    } catch (error) {
      console.error("Erreur updateSettingsAlert:", error);
      setError("Erreur pendant l’enregistrement du message d’alerte.");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    setError("");
    setSaving(true);

    try {
      const res = await disableSettingsAlert();

      if (!res?.ok) {
        throw new Error("disableSettingsAlert failed");
      }

      setForm((current) => ({
        ...current,
        alert_is_active: false,
      }));
    } catch (error) {
      console.error("Erreur disableSettingsAlert:", error);
      setError("Erreur pendant la désactivation du message d’alerte.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Message d’alerte"
      description="Bandeau affiché en haut de la page d’accueil. Idéal pour météo, travaux, réunion publique ou information urgente. Attention, ce message ne sera pas visible en dehors des dates de début et de fin si elles sont renseignées."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleDisable}
            disabled={saving}
            className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Désactiver
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700 disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        <label className="flex items-center justify-between rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-950">
          <div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Statut
            </div>

            <div className="font-bold text-slate-950 dark:text-slate-50">
              {form.alert_is_active ? "Actif" : "Inactif"}
            </div>
          </div>

          <button
            type="button"
            onClick={onToggleActive}
            className="relative inline-flex h-8 w-14 items-center rounded-full bg-slate-300 dark:bg-slate-700"
            title="Activer / Désactiver"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-sky-600 transition ${
                form.alert_is_active ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Type
          </span>

          <select
            name="alert_type"
            value={form.alert_type || "info"}
            onChange={onChange}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-sky-950"
          >
            <option value="info">Information</option>
            <option value="warning">Alerte</option>
            <option value="danger">Urgent</option>
            <option value="success">Bonne nouvelle</option>
          </select>
        </label>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          label="Titre"
          name="alert_title"
          value={form.alert_title}
          onChange={onChange}
          placeholder="Ex : Alerte intempéries"
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="Début"
            name="alert_start_at"
            type="datetime-local"
            value={form.alert_start_at || ""}
            onChange={onChange}
            hint="Vide = immédiat"
          />

          <Input
            label="Fin"
            name="alert_end_at"
            type="datetime-local"
            value={form.alert_end_at || ""}
            onChange={onChange}
            hint="Vide = pas de fin"
          />
        </div>

        <div className="md:col-span-2">
          <Textarea
            label="Message"
            name="alert_message"
            value={form.alert_message}
            onChange={onChange}
            rows={5}
            placeholder="Ex : Fortes rafales attendues. Évitez les déplacements non essentiels."
            hint="Les retours à la ligne sont conservés."
          />
        </div>

        <Input
          label="Texte du lien"
          name="alert_link_label"
          value={form.alert_link_label}
          onChange={onChange}
          placeholder="Ex : En savoir plus"
        />

        <Input
          label="URL du lien"
          name="alert_link_url"
          value={form.alert_link_url}
          onChange={onChange}
          placeholder="https://..."
        />
      </div>

      <div className="mt-6">
        <div className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">
          Aperçu
        </div>

        <div
          className={`rounded-3xl border p-4 ${meta.box} ${
            preview.active ? "" : "opacity-60"
          }`}
        >
          <div className="flex items-start gap-3">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-bold ${meta.pill}`}
            >
              {meta.label}
            </span>

            <div className="min-w-0">
              {preview.title ? (
                <div className="font-bold break-words">{preview.title}</div>
              ) : null}

              <div className="mt-1 whitespace-pre-wrap break-words text-sm leading-6">
                {preview.message || "Votre message d’alerte apparaîtra ici."}
              </div>

              {preview.linkLabel && preview.linkUrl ? (
                <div className="mt-2 text-sm font-bold underline underline-offset-4">
                  {preview.linkLabel}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}